/**
 * # HERE BE DRAGONS!
 *
 * There are a lot of crazy workarounds in this file to battle with
 * restrictions on React hooks, as well as means to ensure that
 * `react-final-form` works correctly when changing the language...
 */
import React, {
  useMemo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef
} from "react";
import { Form } from "react-final-form";
import { TransformMap } from "objectro/lib/types";
import { transform } from "objectro";
import Ajv, { ErrorObject } from "ajv";
import set from "lodash/set";
import cn from "clsx";

import {
  JSONSchema7Object,
  I18nMessage,
  ConfigurableFormValidateFn,
  ConfigurableFormTFn,
  ConfigurableFormLabels,
  ConfigurableFormElements,
  ConfigurableFormConfig,
  ConfigurableFormStepConfig,
  ConfigurableFormElementConfig,
  ConfigurableFormErrorsObject
} from "../../types";
import {
  get,
  has,
  isString,
  isObject,
  findByPropFn,
  isArray
} from "../../utils/helpers";
import { extendAjv } from "../../utils/schemas";
import Button from "../Button";
import Field from "../Field/FinalFormAdaptor";
import SectionHeader from "./SectionHeader";
import SectionText from "./SectionText";
import Group from "./Group";
import FormElements from "./FormElements";
import UpdateFormState, { updateFormState } from "./UpdateForm";
import "./ConfigurableForm.css";
import debug from "../../utils/debug";

export interface ConfigurableFormProps {
  /** The form's ID. */
  id?: string;
  /** The form's initial values. */
  initialValues?: object;
  /** Shared state that elements can use to affect if they're rendered, or when mapping element's state to props. */
  sharedState?: {
    appState: object;
    pageState: object;
    formState: object;
  };
  /** The form's configuration object. */
  formConfig: ConfigurableFormConfig;
  /** The translation function. */
  t?: ConfigurableFormTFn | Function;
  /** The current language. */
  lang?: string;
  /** An object with the elements for rendering. */
  elements?: ConfigurableFormElements;
  /** An object with labels for the form's buttons. */
  labels?: ConfigurableFormLabels;
  /** Any extra classes to give to the form. */
  className?: string;
  /** Validate the form when each input field is blurred. */
  validateOnBlur?: boolean;
  /** Map the form's state to attributes to apply specific rules for localisation via CSS classes. */
  stateToAttributes?: TransformMap;
  /** The event handler to submit the data. */
  onSubmit: (formState: object) => void;
  enableReset?: boolean;
  /** If you want to output your own buttons to submit the form. */
  children?: React.ReactNode | React.ReactNodeArray;
}

export interface ConfigurableFormRef {
  step: number;
  maxStep: number;
  sharedState: object;
  lang: string;
  values: object;
  errors: ConfigurableFormErrorsObject;
  submit: (formState: object) => void;
  validate: (formState: object) => ConfigurableFormErrorsObject;
}

const DEFAULT_AJV_OPTIONS: Ajv.Options = {
  verbose: process.env.NODE_ENV === "development",
  allErrors: true,
  errorDataPath: "property",
  extendRefs: true
};

export const DEFAULT_ELEMENTS: ConfigurableFormElements = {
  SectionHeader,
  SectionText,
  Group,
  Field
};

export const DEFAULT_LABELS: ConfigurableFormLabels = {
  buttonBack: "Back",
  buttonNext: "Continue",
  buttonSubmit: "Submit"
};

export const DEFAULT_T: ConfigurableFormTFn = (
  i18nMessage: string | I18nMessage
): string => {
  // Nothing to translate
  if (!i18nMessage) {
    return "";
  }

  // Use the translation ID given (this is a dummy function anyway)
  if (isString(i18nMessage)) {
    return String(i18nMessage);
  } else {
    return String((i18nMessage as I18nMessage).translationId);
  }
};

/**
 * Takes an object that contains labels and runs the translation function on them.
 * @param labels
 * @param t
 */
export function translateLabels(
  labels: { [labelName: string]: string | I18nMessage | undefined },
  t: Function
) {
  return Object.keys(labels).reduce((_labels, labelName) => {
    set(_labels, labelName, t(get(labels, labelName)));
    return _labels;
  }, {});
}

/**
 * Takes an object that contains options and runs the translation function
 * on each option's label.
 * @param options
 * @param t
 */
export function translateOptionsLabels(
  options: {
    label: string | I18nMessage | undefined;
    value: any;
  }[],
  t: Function
) {
  return options.map(
    ({ label, value }) => ({
      label: t(label),
      value
    }),
    {}
  );
}

/**
 * Attempts to point to any fields within the browser window that have errors.
 * @param formId
 */
function pointOutVisibleErrors(formId: string) {
  const errorNode = document.querySelector(`#${formId} .field.has-errors`);
  if (errorNode && errorNode.scrollIntoView) {
    errorNode.scrollIntoView({
      block: "center"
    });

    const inputNode: HTMLInputElement = errorNode.querySelector(
      "input, textarea, select"
    ) as HTMLInputElement;
    if (inputNode && inputNode.focus) {
      inputNode.focus();
    }
  }
}

/**
 * Map any AJV errors to the form's errors for displaying.
 *
 * If any fields have errorMessage config, it will also map those custom error
 * messages, as well as translate them.
 */
function setFieldErrorFromAjv({
  ajvError,
  errors,
  fields,
  t
}: {
  ajvError: ErrorObject;
  errors: object;
  fields?: ConfigurableFormElementConfig[];
  t: Function;
}) {
  const dataPath =
    ajvError.dataPath.charAt(0) === "."
      ? ajvError.dataPath.slice(1)
      : ajvError.dataPath;

  // Only set the field error if it hasn't got one already.
  // This prevents showing multiple duplicate errors depending on how
  // the schema has been spec'd out
  if (!dataPath || get(errors, dataPath)) {
    return errors;
  }

  // Use AJV's error message as the default message
  let errorMessage = ajvError.message;

  // Find the field by the dataPath to use any error messages
  if (fields) {
    const fieldConfig = fields.find(f => f.name === dataPath);

    // Get the error message from the field's config
    if (fieldConfig && has(fieldConfig, "errorMessage", isString)) {
      errorMessage = t(get(fieldConfig, "errorMessage"));
    }

    // Get the specific keyword error message from the field's config
    if (fieldConfig && has(fieldConfig, "errorMessage", isObject)) {
      // Show a specific error message for the keyword
      if (has(fieldConfig, ["errorMessage", ajvError.keyword], isString)) {
        errorMessage = t(get(fieldConfig, ["errorMessage", ajvError.keyword]));
      }
      // Show a generic error message if an error message for the specific keyword doesn't exist
      else if (has(fieldConfig, "errorMessage._", isString)) {
        errorMessage = t(get(fieldConfig, "errorMessage._"));
      }
    }
  }

  // Add the error to the field (and form's) errors
  const fieldErrors = get(errors, dataPath, []);
  fieldErrors.push(errorMessage);
  set(errors, dataPath, fieldErrors);

  return errors;
}

/**
 * Builds a validation function that uses AJV if schemas are given.
 */
function buildValidateFn({
  schema = {},
  refSchemas = [],
  fields,
  lang,
  t
}: {
  schema: JSONSchema7Object;
  refSchemas?: JSONSchema7Object[];
  fields?: ConfigurableFormElementConfig[];
  lang: string;
  t: Function;
}): ConfigurableFormValidateFn {
  // AJV is used to validate the schema
  const ajv = new Ajv({
    ...DEFAULT_AJV_OPTIONS,
    schemas: [schema, ...refSchemas]
  });

  // Extend AJV
  extendAjv(ajv);

  // Run the validation on the current step
  return (values: object): ConfigurableFormErrorsObject => {
    values = { ...values, preferredLanguage: lang };
    const isValid = ajv.validate(schema, values);
    const hasErrors = ajv.errors ? ajv.errors : [];

    if (isValid) {
      return {};
    }

    // @NOTE This is a good thing to keep in the back pocket while
    // developing and debugging validation schemas!
    debug("[ConfigurableForm] debug Ajv validation", {
      values,
      isValid,
      hasErrors,
      schema,
      ajv
    });

    return hasErrors.reduce(
      (errors, ajvError) =>
        setFieldErrorFromAjv({ ajvError, errors, fields, t }),
      {}
    );
  };
}

export default function ConfigurableForm({
  id = `configurable-form-${+new Date()}`,
  initialValues = {},
  sharedState = {
    appState: {},
    pageState: {},
    formState: {}
  },
  formConfig,
  className,
  validateOnBlur = false,
  labels = {},
  elements = DEFAULT_ELEMENTS,
  lang = "en",
  t = DEFAULT_T,
  stateToAttributes,
  onSubmit,
  enableReset,
  children
}: ConfigurableFormProps) {
  // State
  const ref = useRef<ConfigurableFormRef>({
    step: 0,
    maxStep: 0,
    sharedState,
    lang,
    values: { ...initialValues },
    errors: {},
    submit: onSubmit,
    validate: () => ({})
  });
  const [, updateState] = React.useState();

  const stepConfig:
    | ConfigurableFormConfig
    | ConfigurableFormStepConfig = useMemo(
    () =>
      has(formConfig, "steps") && formConfig.steps
        ? formConfig.steps[ref.current.step]
        : formConfig,
    [ref.current.step, formConfig]
  );

  const fields: ConfigurableFormElementConfig[] = stepConfig.fields || [];

  // Flatten the fields for some auxiliary functions to not have to expense too much effort to do their thing
  const flattenedFields: ConfigurableFormElementConfig[] = useMemo(() => {
    const hasNestedFields = fields.filter(findByPropFn("fields", isArray));
    if (hasNestedFields.length) {
      return [
        ...fields,
        ...hasNestedFields.reduce(
          (acc, field) => acc.concat(get(field, "fields")),
          []
        )
      ];
    } else {
      return fields;
    }
  }, [fields]);

  // Since I only want to run this as when needed and set the ref,
  // we don't use the memoised version.
  useMemo(() => {
    const _maxStep =
      has(formConfig, "steps") && formConfig.steps
        ? formConfig.steps.length - 1
        : 0;
    ref.current.maxStep = _maxStep;
  }, [formConfig]);

  const translatedLabels: { [labelName: string]: string } = useMemo(
    () =>
      translateLabels(
        {
          ...DEFAULT_LABELS,
          ...formConfig.labels,
          ...stepConfig.labels,
          ...labels
        },
        t
      ),
    [t, lang, stepConfig, labels]
  );

  // Memoise the current validateSchema
  const validateSchema = useMemo(
    () =>
      !!stepConfig.validateSchema
        ? stepConfig.validateSchema
        : formConfig.validateSchema,
    [ref.current.step]
  );

  // Each step can have different validation rules
  // Since I only want to run this as when needed and set the ref,
  // we don't use the memoised version.
  useMemo(() => {
    const _validate = !!formConfig.validate
      ? formConfig.validate
      : !!validateSchema
      ? buildValidateFn({
          schema: validateSchema,
          refSchemas: formConfig.refSchemas,
          fields: flattenedFields,
          lang,
          t
        })
      : (): ConfigurableFormErrorsObject => ({});
    ref.current.validate = _validate;
    return _validate;
  }, [t, lang, formConfig, validateSchema, flattenedFields]);

  const handleClickNextButton = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    const visibleErrorNode = document.querySelector(".field.has-errors");
    debug("[ConfigurableForm] errors:", visibleErrorNode);
    if (visibleErrorNode) {
      event.preventDefault();
      pointOutVisibleErrors(id);
    }
  };

  const handleResetButton = () => {
    Object.keys(ref.current.values).forEach((fieldName: string) => {
      const value = get(initialValues, fieldName);
      set(ref.current.values, fieldName, value);
    });
    handleFormValidate(ref.current.values);
    updateState({});
  };

  // Since React-Final-Form takes a function only in the initialisation phase
  // we can memoise this and use the ref to reference the step's current
  // validate function.
  const handleFormValidate = useCallback((values: object): object => {
    //debug("[ConfigurableForm] handleFormValidate:", values);
    // Point to the latest validate function
    const validateFn = ref.current.validate;

    // Ensure to feed through the shared state for validation purposes
    const validateState = {
      ...values,
      appState: get(ref, "current.sharedState.appState"),
      pageState: get(ref, "current.sharedState.pageState")
    };
    const valid = validateFn(validateState);
    debug("[ConfigurableForm] valid:", valid);
    return valid;
  }, []);

  // Since React-Final-Form takes a function only in the initialisation phase
  // we can memoise this and use the ref to reference the step's current
  // submit function.
  const handleFormSubmit = useCallback((values: object) => {
    debug("[ConfigurableForm] handleFormSubmit:", values);
    const submitFn = ref.current.submit;

    // restore missing keys (of cleared fields) to prevent restore of initial value
    Object.keys(initialValues).forEach(key => {
      if (get(values, key) === undefined) {
        set(values, key, null);
      }
    });

    // Move to the next step (if there are other steps to go to)
    if (
      ref.current.maxStep !== ref.current.step &&
      ref.current.step < ref.current.maxStep
    ) {
      ref.current.maxStep = ref.current.step + 1;
    } else {
      submitFn(values);
    }
  }, []);

  useEffect(() => {
    const formNode = document.getElementById(id);
    if (formNode && formNode.scrollIntoView) {
      formNode.scrollIntoView({
        block: "start"
      });
    }
  }, [id]);

  // Update the errors before re-rendering, only if the t/language changed.
  // This is required because if we change the language, Final form doesn't
  // attempt to re-validate the form, so we validate the form manually
  // in order to have correctly translated error messages.
  useLayoutEffect(() => {
    ref.current.errors = ref.current.validate(ref.current.values);
  }, [t, lang]);

  // init tags fields (preferences) - must be array
  const initTagsValues = (
    values: object,
    fields: ConfigurableFormElementConfig[]
  ) => {
    fields.forEach(field => {
      if (
        field.component === "Field" &&
        field.type === "tags" &&
        !get(values, field.id!)
      ) {
        set(values, field.id!, []);
      }
      if (field.component === "Group" && field.fields) {
        initTagsValues(values, field.fields);
      }
    });
  };
  initTagsValues(ref.current.values, fields);

  return (
    <Form
      // Using a key based on the language ensures the final form re-renders
      // when the language changed, however we need to use the saved values
      // (stored in a ref to avoid any recursive re-rendering being triggered)
      // to reinitialise the form's data, or else it would have used `initialValues`
      key={`${lang}-configurable-form`}
      initialValues={ref.current.values}
      validateOnBlur={validateOnBlur}
      validate={handleFormValidate}
      onSubmit={handleFormSubmit}
      mutators={{ updateFormState }}
    >
      {({
        handleSubmit,
        // active,
        // dirty,
        // dirtyFields,
        // dirtySinceLastSubmit,
        // error,
        errors,
        // hasSubmitErrors,
        // hasValidationErrors,
        // initialValues,
        // invalid,
        // modified,
        // pristine,
        // submitError,
        // submitErrors,
        // submitFailed,
        // submitSucceeded,
        submitting,
        // touched,
        // valid,
        // validating,
        values,
        form
        // visited
      }) => {
        const _sharedState = {
          ...sharedState,
          formState: values
        };
        ref.current.values = values;
        ref.current.errors = errors;
        ref.current.sharedState = _sharedState;

        // Form attributes to affect localisation
        const formAttributes = stateToAttributes
          ? transform(_sharedState, stateToAttributes)
          : undefined;

        return (
          <form
            {...formAttributes}
            lang={lang}
            id={id}
            className={cn("configurable-form", className)}
            autoComplete="off"
            onSubmit={handleSubmit}
          >
            {!formConfig.buttonsOnTop && (
              <FormElements
                sharedState={_sharedState}
                errors={ref.current.errors}
                fields={fields}
                elements={elements}
                t={t as ConfigurableFormTFn}
              />
            )}
            {!children && (
              <div className="configurable-form-controls button-group">
                {enableReset && (
                  <Button
                    type="reset"
                    theme="neutral"
                    shape="solid"
                    className="button-role-reset"
                    label="reset"
                    disabled={submitting}
                    onClick={handleResetButton}
                  />
                )}
                <Button
                  type="submit"
                  theme="primary"
                  shape="solid"
                  icon={submitting ? "loading" : undefined}
                  className="button-role-submit"
                  label={
                    ref.current.step === ref.current.maxStep
                      ? translatedLabels.buttonSubmit
                      : translatedLabels.buttonNext
                  }
                  disabled={submitting}
                  fullWidth
                  onClick={handleClickNextButton}
                />
              </div>
            )}
            <br />
            <p>{t("general.fields.requiredcation.label")}</p>
            {formConfig.buttonsOnTop && (
              <FormElements
                sharedState={_sharedState}
                errors={ref.current.errors}
                fields={fields}
                elements={elements}
                t={t as ConfigurableFormTFn}
              />
            )}
            {children}
            <UpdateFormState mutators={form.mutators} />
          </form>
        );
      }}
    </Form>
  );
}
